import { Box, Container } from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllUpdates, getCategories } from '../data/updatesData';
import { UpdateCard, UpdatesHeading } from './atomic';
import Footer from './Footer';
import Header from './Header';
import { StyledButton } from './ui';

/**
 * UpdatesPage - Full page for all Research, Media, Stories, and Community content
 *
 * @returns {JSX.Element} Updates page with all content items
 */
function UpdatesPage() {
  const _navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('All');

  // Get all updates from shared data source
  const allUpdates = getAllUpdates();

  // Get categories dynamically from data
  const categories = getCategories();
  const filterOptions = [...categories, 'All'];

  // Filter the updates based on active filter
  const filteredUpdates =
    activeFilter === 'All'
      ? allUpdates
      : allUpdates.filter((update) => update.category === activeFilter);

  return (
    <>
      <Header />
      <Box
        sx={{
          width: '100vw',
          backgroundColor: 'var(--color-background-light)',
          minHeight: '100vh',
          pt: { xs: 8, md: 10 },
          pb: 8,
        }}
      >
        {/* Header with dark theme */}
        <Box
          sx={{
            borderTop: '4px solid var(--color-primary)',
            borderBottom: '4px solid var(--color-primary)',
            backgroundColor: 'var(--color-secondary)',
            py: 4,
            mb: 6,
          }}
        >
          <UpdatesHeading
            component="h1"
            fontSize={{ xs: '2.5rem', md: '3.5rem' }}
            introFontSize="1.25rem"
          />
        </Box>

        {/* Filter buttons */}
        <Container maxWidth="lg" sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              justifyContent: 'center',
              flexWrap: 'wrap',
            }}
          >
            {filterOptions.map((filter) => (
              <StyledButton
                key={filter}
                variant={activeFilter === filter ? 'contained' : 'outlined'}
                onClick={() => setActiveFilter(filter)}
                sx={{
                  backgroundColor:
                    activeFilter === filter
                      ? 'var(--color-primary)'
                      : 'transparent',
                  borderColor: 'var(--color-primary)',
                  color:
                    activeFilter === filter
                      ? 'var(--color-primary-text)'
                      : 'var(--color-primary)',
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor:
                      activeFilter === filter
                        ? 'var(--color-primary-dark)'
                        : 'color-mix(in srgb, var(--color-primary) 10%, transparent)',
                  },
                }}
              >
                {filter === 'All' ? 'All Updates' : filter}
              </StyledButton>
            ))}
          </Box>
        </Container>

        {/* Main content grid */}
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                md: 'repeat(3, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
              gap: 4,
            }}
          >
            {filteredUpdates.map((update, index) => (
              <UpdateCard
                key={`${update.title}-${index}`}
                {...update}
                index={index}
              />
            ))}
          </Box>
        </Container>
      </Box>
      <Footer />
    </>
  );
}

export default UpdatesPage;
